// Copyright (C) 2023, Rutio AB. All rights reserved

/**
 * Sideways View — 2D side-projection of device positions on a vessel/structure.
 *
 * This module renders an interactive side-view image of a vessel (ship, building, etc.)
 * with device positions plotted on it. GPS coordinates are projected onto a 2D image
 * using reference points and geometric calculations.
 *
 * ## Architecture
 *
 * 1. **Metadata** — Fetched from backend (`/sidewaysdata`), contains:
 *    - Image dimensions and reference point GPS coordinates (left/right)
 *    - Floor-to-Y-pixel mapping (`floor_index_y_mapping`)
 *    - Marker size and per-floor X-axis limits
 *    - Actual vessel length in metres for distance scaling
 *
 * 2. **Coordinate Projection** (see `generateItem`):
 *    - Y-axis: Determined by device `floor` via `floor_index_y_mapping`
 *    - X-axis: Uses triangle geometry (Haversine distances + Heron's formula)
 *      to project device GPS onto the left-right axis of the vessel image
 *
 * 3. **Components:**
 *    - `SideWays` — Main component, loads metadata and renders the image with markers
 *    - `SideMarker` — Individual device marker with expand/collapse for stacked items
 *    - `Popped` — Device detail popup with actions (check-in, trace, alarm)
 *    - `generateItem` — Core projection function (GPS → pixel coordinates)
 *
 * @module Sideways
 */

import React, { useEffect, useState } from 'react';
import type { ReactElement } from 'react';

import { getApi } from './api';
import { ClickOnceButton } from './ClickOnceButton';
import { AREA_SYMBOLS } from './components/icons/AreaSymbols';
import { Fetch } from './Fetch';
import {
  alarmColor,
  traceColor,
  deviceColor,
  checkColor,
  selectColor,
  smallFontSize,
  smallTextStyle,
  checkpointColor,
} from './styles/constants';
import type {
  SideWaysProps,
  SideMarkerProps,
  PoppedProps,
  SidewaysMetadata,
  ItemPosition,
  PoppedItem,
  BaseMapItem,
  WindowDimensions,
  Device,
} from './types';
import { geofenceAreaConfigAPI } from './utils/apiClient';
import { formatDateAsAge } from './utils/dateUtils';
import { isBatteryLow } from './utils/deviceUtils';
import { getMaxAlarmTimeS, getMaxTraceTimeS } from './utils/limits';
import logger from './utils/logger';
import { calculateDistance } from './utils/trigonometrics';

const ec = (s: string): string => encodeURIComponent(s);

const params = new URLSearchParams(window.location.search);
const displayReplacedPositions = params.get('displayReplaced')
  ? params.get('displayReplaced') === 'true'
  : false;

// Just a function wrapper in case we replace this with a setting later
const getDisplayReplacedPositions = (): boolean => {
  return displayReplacedPositions;
};

const getWindowDimensions = (): WindowDimensions => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

const showAsAlarm = (item: BaseMapItem | null): boolean => {
  const now = new Date();
  if (item && item.alarmTime) {
    const ageS = (now.getTime() - new Date(item.alarmTime).getTime()) / 1000;
    return ageS < getMaxAlarmTimeS() ? true : false;
  }
  return false;
};

const showAsTrace = (item: BaseMapItem | null): boolean => {
  const now = new Date();
  if (item && item.traceTime) {
    const ageS = (now.getTime() - new Date(item.traceTime).getTime()) / 1000;
    return ageS < getMaxTraceTimeS() ? true : false;
  }
  return false;
};

const showAsChecked = (item: BaseMapItem | null): boolean => {
  if (item && item.checkinTime) return true;
  return false;
};

const nibbleToHex = (x: number): string => {
  const table = '0123456789abcdef';
  x &= 0xf;
  return table[x];
};

/**
 * Add transparency to a 4-character hex color based on position age.
 *
 * Older positions fade out linearly over `maxAge` seconds, from full opacity
 * (0xF) to ~50% opacity (0x7). Uses a single hex nibble appended to the
 * short-form color (e.g. "#F00" → "#F008" for 50% transparent).
 *
 * @param maxAge - Maximum age in seconds for full fade
 * @param item - Map item with positionTime
 * @param color - 4-char hex color string (e.g. "#F00")
 * @param multi - If true, skip transparency (stacked markers stay opaque)
 * @returns Color string with appended opacity nibble
 */
const addAgeTransparency = (
  maxAge: number,
  item: BaseMapItem | null,
  color: string,
  multi?: boolean
): string => {
  if (typeof color !== 'string' || color.length !== 4) return color;
  if (!(item && item.positionTime)) return color;
  if (multi) return color;
  const nowS = new Date().getTime() / 1000;
  const itemS = new Date(item.positionTime).getTime() / 1000;
  let age = nowS - itemS;
  if (age < 0) age = 0;
  if (age >= maxAge) age = maxAge;
  const fraction = Math.floor((8 * age) / maxAge);
  const opacity = nibbleToHex(0xf - fraction);

  return color + opacity;
};

const getDeviceColor = (device: BaseMapItem | null): string => {
  if (showAsAlarm(device)) return traceColor;
  if (device && device.checkpoint) return checkpointColor;
  if (showAsTrace(device)) return traceColor;
  return deviceColor;
};

const generateCheckinBubble = (
  size: number,
  checkinCount: number,
  xpos: number,
  ypos: number
): ReactElement => {
  return (
    <div
      style={{
        width: (3 * size) / 4,
        height: (3 * size) / 4,
        borderRadius: size / 2,
        background: checkColor,
        zIndex: 2,
        borderStyle: 'solid',
        borderColor: 'black',
        borderWidth: '1px',
        position: 'fixed',
        top: ypos - (6 * size) / 8 + 'px',
        left: xpos + size / 6 + 'px',
        fontSize: size / 1.6,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      {checkinCount}
    </div>
  );
};

const generateAlarmSymbol = (
  size: number,
  alarmCount: number,
  xpos: number,
  ypos: number
): ReactElement => {
  return (
    <div
      style={{
        width: (3 * size) / 4,
        height: (3 * size) / 4,
        borderRadius: size / 2,
        background: alarmColor,
        zIndex: 2,
        borderStyle: 'solid',
        borderColor: 'black',
        borderWidth: '1px',
        position: 'fixed',
        top: ypos - (6 * size) / 8 + 'px',
        left: xpos + size / 6 + 'px',
        fontSize: size / 1.6,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      {alarmCount}
    </div>
  );
};

const SideMarker: React.FC<SideMarkerProps> = (props) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [itemsOnSamePosition] = useState<ItemPosition[]>(props.itemsOnSamePosition);
  const [imageScale] = useState<number>(props.imageScale);
  const [width] = useState<number>(props.width);
  const [height] = useState<number>(props.height);
  const [maxAge] = useState<number>(props.maxAge);
  const [count] = useState<number>(
    props.itemsOnSamePosition ? props.itemsOnSamePosition.length : 1
  );
  const [posTS] = useState(props.item.positionTime);
  const [itemTS] = useState(props.item.timestamp);
  const [maxAgePct] = useState(props.item.positionTime);
  const [alarmTime] = useState(props.item.alarmTime);
  // Using useState to track changes in position, timestamp, etc.
  useState('' + posTS + itemTS + maxAgePct + alarmTime + count);
  const [size] = useState<number>(
    props.metadata && props.metadata.marker_size
      ? props.metadata.marker_size * imageScale
      : imageScale * 15
  );
  // Track position changes
  useState('' + props.xpos + ',' + props.ypos);
  const [scroll, setScroll] = useState<number>(0);
  const [touchY, setTouchY] = useState<number>(0);

  let checkpointCount = 0;
  let checkinCount = 0;
  let traceCount = 0;
  let alarmCount = 0;

  let k: string;
  if (itemsOnSamePosition) {
    const arr = itemsOnSamePosition.map((i, ix) => {
      if (showAsChecked(i.item)) checkinCount++;
      if (showAsAlarm(i.item)) alarmCount++;
      if (showAsTrace(i.item)) traceCount++;
      return (
        '' +
        ix +
        (showAsChecked(i.item) ? 'C' : 'c') +
        (showAsAlarm(i.item) ? 'A' : 'a') +
        (showAsTrace(i.item) ? 'T' : 't')
      );
    });
    k = arr.join('');
    itemsOnSamePosition.map((i) => (checkpointCount += i.item.checkpoint ? 1 : 0));
  } else {
    k =
      (showAsAlarm(props.item) ? 'A' : '-') +
      (showAsChecked(props.item) ? 'C' : '-') +
      showAsTrace(props.item)
        ? 'T'
        : 't';
    if (showAsChecked(props.item)) checkinCount = 1;
    if (showAsAlarm(props.item)) alarmCount = 1;
    if (showAsTrace(props.item)) traceCount = 1;
  }

  // These variables affect the state of this devices icon
  useState(checkinCount);
  useState(checkpointCount);
  useState(traceCount);
  useState(alarmCount);

  const onScrollDelta = (delta: number): void => {
    if (scroll + delta / 10 <= 0) setScroll(0);
    else if (
      scroll + delta / 10 >= itemsOnSamePosition.length - 10 &&
      itemsOnSamePosition.length > 10
    )
      setScroll(itemsOnSamePosition.length - 10);
    else if (itemsOnSamePosition.length > 10) setScroll(scroll + delta / 10);
  };

  if (expanded) {
    let leftRightStyle: React.CSSProperties = { left: props.xpos - size / 2 + 'px' };
    let topBottomStyle: React.CSSProperties = { bottom: height - props.ypos - size / 2 + 'px' };
    if (props.xpos > width / 2) leftRightStyle = { right: width - (props.xpos + size / 2) + 'px' };
    if (props.ypos < height / 2) topBottomStyle = { top: props.ypos - size / 2 + 'px' };
    return (
      <div
        key={k}
        style={{
          zIndex: 2,
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          background: '#fffc',
        }}
        onClick={() => setExpanded(false)}
      >
        <div
          style={{
            position: 'fixed',
            touchAction: 'none',
            ...topBottomStyle,
            ...leftRightStyle,
            background: '#eee6',
            borderStyle: 'solid',
            padding: '2px',
            borderWidth: '1px',
            borderRadius: '5px',
          }}
          onClick={(e) => {
            e.stopPropagation();
            props.setPopped(null);
            setExpanded(false);
            setScroll(0);
          }}
          onWheel={(e) => {
            onScrollDelta(e.deltaY);
          }}
          onTouchStart={(e) => setTouchY(e.touches[e.touches.length - 1].screenY)}
          onTouchMove={(e) => {
            const delta = touchY - e.touches[e.touches.length - 1].screenY;
            onScrollDelta(delta / 2);
            setTouchY(e.touches[e.touches.length - 1].screenY);
          }}
        >
          <div style={{ width: '30vw', fontSize: size - 2 }}>
            Showing {Math.floor(scroll) + 1}-
            {Math.min(Math.floor(scroll + 10), itemsOnSamePosition.length)} of{' '}
            {itemsOnSamePosition.length} at position
          </div>
          <hr style={{ color: '#cccc' }} />
          {itemsOnSamePosition.map((i, ix) =>
            ix >= Math.floor(scroll) && ix < Math.floor(scroll) + 10 ? (
              <div
                key={String(height) + String(i.item.id)}
                style={{ display: 'flex', flexDirection: 'row', zIndex: 2000, fontSize: size - 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(false);
                  setScroll(0);
                  props.setPopped({ item: i.item, y: props.ypos, x: props.xpos });
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: size + 'px',
                    height: size + 'px',
                    margin: '1px',
                    borderRadius: '.5vmin',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'black',
                    background: i.item.checkpoint
                      ? checkpointColor
                      : addAgeTransparency(maxAge, i.item, getDeviceColor(i.item)),
                  }}
                >
                  {
                    /* Add alarm status? */
                    showAsAlarm(i.item) ? (
                      <div
                        style={{
                          width: size / 4 + 'px',
                          float: 'right',
                          height: size / 4 + 'px',
                          borderRadius: size / 6 + 'px',
                          background: alarmColor,
                          borderStyle: 'solid',
                          borderColor: 'black',
                          borderWidth: '1px',
                          padding: '1px',
                        }}
                      />
                    ) : null
                  }
                  {
                    /* Add checkin status? */
                    showAsChecked(i.item) ? (
                      <div
                        style={{
                          width: size / 4 + 'px',
                          float: 'right',
                          height: size / 4 + 'px',
                          borderRadius: size / 6 + 'px',
                          background: checkColor,
                          borderStyle: 'solid',
                          borderColor: 'black',
                          borderWidth: '1px',
                          padding: '1px',
                        }}
                      />
                    ) : null
                  }
                  {i.item.area && props.areaSymbols[i.item.area]
                    ? (() => {
                        const SymbolComponent = AREA_SYMBOLS[props.areaSymbols[i.item.area!]];
                        return SymbolComponent ? (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}
                          >
                            <SymbolComponent size={size * 0.85} />
                          </div>
                        ) : null;
                      })()
                    : null}
                </div>
                {i.item.title +
                  (i.item.area
                    ? ' · ' + i.item.area
                    : i.item.frame != null &&
                        i.item.positionType !== 'GEOFENCE' &&
                        i.item.positionType !== 'checkin_station'
                      ? ' · Frame ' + (i.item.frame - 10) + '–' + (i.item.frame + 10)
                      : '') +
                  (i.item.checkpoint
                    ? ''
                    : ' (' + formatDateAsAge(new Date(i.item.positionTime || new Date())) + ')')}
              </div>
            ) : null
          )}
        </div>
      </div>
    );
  }

  const makeCheckinBubble = (): ReactElement => {
    return generateCheckinBubble(size / 1.2, checkinCount, props.xpos + size / 8, props.ypos);
  };

  const makeAlarmSymbol = (): ReactElement => {
    return generateAlarmSymbol(
      size / 1.2,
      alarmCount,
      props.xpos + size / 8,
      props.ypos + size / 1.5
    );
  };

  const getPileColor = (): string => {
    if (alarmCount > 0) return traceColor;
    if (traceCount > 0) return traceColor;
    if (checkpointCount === itemsOnSamePosition.length) return checkpointColor;
    return deviceColor;
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (count === 1) {
          props.setPopped({ item: props.item, y: props.ypos, x: props.xpos });
        } else {
          props.setPopped(null);
          setExpanded(!expanded);
          setScroll(0);
        }
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: props.ypos - size / 2 + 'px',
          left: props.xpos - size / 2 + 'px',
          fontSize: smallFontSize,
        }}
        key={'' + k + height + props.item.id + count}
      >
        <div
          key={'label' + k}
          style={{
            zIndex: 0,
            position: 'fixed',
            top: props.ypos + size / 2 + 'px',
            left: props.xpos - size / 2 + 'px',
            fontSize: (2 * size) / 3,
            textAlign: 'left',
            fontWeight: 600,
            color: '#000',
            background: '#fff9',
          }}
        >
          {itemsOnSamePosition.map((i) => {
            if (
              new Date().getTime() - new Date(i.item.positionTime || new Date()).getTime() >
              2 * 60 * 1000
            ) {
              // Check if positionTime is older than 2 minutes
              if (showAsAlarm(i.item)) {
                return (
                  <div key={i.item.id + '-alarm'} style={{ color: alarmColor }}>
                    {i.item.title +
                      ' (' +
                      formatDateAsAge(new Date(i.item.positionTime || new Date())) +
                      ')'}
                  </div>
                );
              } else if (showAsTrace(i.item)) {
                return (
                  <div key={i.item.id + '-trace'} style={{ color: traceColor }}>
                    {i.item.title +
                      ' (' +
                      formatDateAsAge(new Date(i.item.positionTime || new Date())) +
                      ')'}
                  </div>
                );
              } /*
           else if (i.item) {
                return <div key={i.item.id + '-device'} style={{color: deviceColor}}>{i.item.title + " (" + formatDateAsAge(new Date(i.item.positionTime)) + ")"}</div>;
            } */ else {
                return null;
              }
            }
            return null;
          })}
        </div>
        <div
          key={'box' + k}
          style={{
            zIndex: 1,
            width: size + 'px',
            height: size + 'px',
            borderRadius: '.6vmin',
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: 'black',
            fontSize: size - 2,
            fontWeight: 600,
            color: 'white',
            textAlign: 'center',
            lineHeight: size + 'px',
            background: addAgeTransparency(
              maxAge,
              props.item,
              props.selected === props.item.id ? selectColor : getPileColor()
            ),
            // CSS outline renders outside the border box on the element itself (same z-index),
            // so it is always visible regardless of scale — unlike a separate sibling div at z=0.
            // outlineOffset:1px keeps the ring tight to the box; checkColor (#4b4) is lighter than
            // checkpointColor (#181) so the ring contrasts visibly against the dark device fill.
            ...(props.item.checkpoint
              ? { outline: `3px solid ${checkColor}`, outlineOffset: '1px' }
              : {}),
          }}
        >
          {count > 1 ? count - checkpointCount : ''}
          {count === 1 && props.item.area && props.areaSymbols[props.item.area]
            ? (() => {
                const SymbolComponent = AREA_SYMBOLS[props.areaSymbols[props.item.area!]];
                return SymbolComponent ? (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <SymbolComponent size={size * 0.85} />
                  </div>
                ) : null;
              })()
            : null}
          {alarmCount > 0 ? makeAlarmSymbol() : null}
          {/* The bubble with checkin count at this position */}
          {checkinCount > 0 ? makeCheckinBubble() : null}
        </div>
      </div>
    </div>
  );
};

/**
 * Compute the horizontal pixel offset for a device that visually collides with
 * items on other decks at the same X position.
 *
 * Checkpoints are fixed infrastructure — their position on the diagram must be
 * exact, so they are never staggered.  Devices carry GPS uncertainty that is
 * already larger than the stagger distance, so a half-marker offset is within
 * the measurement error and keeps every marker individually tappable.
 *
 * Direction: items higher on screen (smaller Y pixel) shift left; items lower
 * shift right — matching the natural reading order of deck numbers.
 */
export const computeCrossLevelStagger = (
  isCheckpoint: boolean,
  x: number,
  y: number,
  crossLevelItems: ReadonlyArray<{ x: number; y: number }>,
  markerSizePx: number
): number => {
  if (isCheckpoint) return 0;
  // Only stagger against items on a different deck (different Y); same-deck items use levelItems grouping
  const neighbors = crossLevelItems.filter(
    (ci) =>
      Math.abs(ci.x - x) < markerSizePx &&
      Math.abs(ci.y - y) > 0 &&
      Math.abs(ci.y - y) < markerSizePx * 2
  );
  if (neighbors.length === 0) return 0;
  const avgNeighborY = neighbors.reduce((s, ci) => s + ci.y, 0) / neighbors.length;
  return (y < avgNeighborY ? -1 : 1) * (markerSizePx / 2);
};

/**
 * Project a device's GPS position onto the 2D side-view image.
 *
 * ## Projection Algorithm
 *
 * **Y-axis (vertical / deck):**
 *   Looked up from `metadata.floor_index_y_mapping` using the device's floor number.
 *   Each floor maps to a fixed Y pixel offset in the image.
 *
 * **X-axis (horizontal / position along vessel):**
 *   Uses triangle geometry with two known GPS reference points (left and right ends):
 *
 *   1. Calculate three Haversine distances:
 *      - a = distance(left_ref, right_ref)  — the vessel length
 *      - b = distance(device, left_ref)
 *      - c = distance(device, right_ref)
 *
 *   2. Use Heron's formula to find the triangle area:
 *      s = (a+b+c)/2, A = √(s(s-a)(s-b)(s-c))
 *
 *   3. Calculate perpendicular height: h = 2A/a
 *      (how far off the vessel centerline the device is — used for validation)
 *
 *   4. Use Pythagoras to find horizontal distance from left reference:
 *      distanceL = √(b² - h²)
 *
 *   5. Convert to pixel X: x = x_left + (distanceL / actual_length_m) * fullXDistancePixels
 *
 * **Stacking:** Multiple devices at the same position are grouped and rendered
 * as a single expandable marker showing the count.
 *
 * @param metadata - Sideways image metadata with reference points and dimensions
 * @param item - Device data with GPS position, floor, alarm/trace state
 * @param imageScale - Scale factor (screen width / image width)
 * @param imageTop - Y offset for centering image vertically
 * @param setPopped - Callback to show device detail popup
 * @param levelItems - Accumulator tracking items per Y-level (for stacking detection)
 * @param width - Screen width in pixels
 * @param height - Screen height in pixels
 * @param selected - Currently selected device ID
 * @param setSelected - Callback to select a device
 * @param maxAge - Max age in seconds for opacity fade
 * @returns ReactElement with positioned marker, or null if position is invalid
 */
const generateItem = (
  metadata: SidewaysMetadata,
  item: BaseMapItem,
  imageScale: number,
  imageTop: number,
  setPopped: (popped: PoppedItem | null) => void,
  levelItems: Record<number, ItemPosition[]>,
  crossLevelItems: Array<{ x: number; y: number }>,
  width: number,
  height: number,
  selected: string | null,
  setSelected: (id: string | null) => void,
  maxAge: number,
  areaSymbols: Record<string, string>
): ReactElement | null => {
  // Calculate y coordinate
  let x = metadata.x_left * imageScale;
  let y = imageTop;
  if (
    typeof metadata.floor_index_y_mapping === 'object' &&
    Object.prototype.hasOwnProperty.call(metadata.floor_index_y_mapping, item.floor ?? '')
  ) {
    y += metadata.floor_index_y_mapping[item.floor ?? 0] * imageScale;
  } else {
    if (item.floor == null || String(item.floor) === '')
      return (
        <div
          key={item.id}
          onClick={() => {
            window.localStorage.setItem('showAccuracy', 'true');
            setSelected(String(item.id));
          }}
        >
          <small>{item.title ? item.title : item.text}: Floor not set</small>
        </div>
      );

    return (
      <div key={item.id}>
        {item.title ? item.title : item.text}: Floor &apos;{item.floor}&apos; not found
      </div>
    );
  }

  // Calculate x coordinate (based on distance)
  let itemlat: number, itemlng: number;
  if (item.latlng && Array.isArray(item.latlng) && item.latlng.length >= 2) {
    itemlat = item.latlng[0];
    itemlng = item.latlng[1];
  } else {
    return (
      <div key={item.id}>
        <small>{item.title ? item.title : item.text}: No position set</small>
      </div>
    );
  }

  let valid = true;
  // Do triangle solution
  const a = calculateDistance(
    metadata.reference_lat_left,
    metadata.reference_lon_left,
    metadata.reference_lat_right,
    metadata.reference_lon_right
  );
  const b = calculateDistance(
    itemlat,
    itemlng,
    metadata.reference_lat_left,
    metadata.reference_lon_left
  );
  const c = calculateDistance(
    itemlat,
    itemlng,
    metadata.reference_lat_right,
    metadata.reference_lon_right
  );
  // logger.debug("a,b,c=", a,b,c);
  if (b > a || c > a) {
    valid = false;
  }

  // Herons formula, calculates triangle area
  const s = (a + b + c) / 2;
  const A = Math.sqrt(s * (s - a) * (s - b) * (s - c));
  const h = (2 * A) / a;
  // logger.debug("A, h, a", A, h, a);
  if (h > a) valid = false;

  // Pythagoras to calculate position on boat
  const distanceL = Math.sqrt(b * b - h * h);
  // logger.debug("distanceL", distanceL);

  // Calculate distance from left corner latlng
  // const distanceL = calculateDistance(itemlat, itemlng, metadata.reference_lat_left, metadata.reference_lon_left);
  if (valid) valid = distanceL >= 0 && distanceL <= metadata.actual_length_m + 2;
  // Calculate distance from right corner latlng
  //const distanceR = calculateDistance(itemlat, itemlng, metadata.reference_lat_right, metadata.reference_lon_right);
  //const RValid = distanceR <= metadata.actual_length_m + 2;

  if (!valid) {
    // logger.debug("Position for " + item.title + " is invalid, distanceL = " + distanceL + "(max " + metadata.actual_length_m + ")");
    if (showAsAlarm(item))
      return (
        <div
          key={item.id}
          onClick={() => {
            setSelected(String(item.id));
            window.localStorage.setItem('showAccuracy', 'true');
          }}
        >
          <small>{item.title ? item.title : item.text} not on site (Trace to check)</small>
        </div>
      );
    else return null;
  }

  const distanceLUse = distanceL;
  /*
  let distanceLUse = 0;
  if (LValid && !RValid)
    distanceLUse =  distanceL;
  else if (RValid && !LValid)
    distanceLUse = metadata.actual_length_m - distanceR;
  else { // Use the longest of the two
    distanceLUse = distanceL > distanceR ? distanceL : (metadata.actual_length_m - distanceR);
  }
  */

  // How many pixels are actual width representing
  const fullXDistancePixels = (metadata.x_right - metadata.x_left) * imageScale;
  let xfraction = distanceLUse / metadata.actual_length_m;
  if (xfraction > 1) {
    xfraction = 1;
    logger.debug('Fraction trunkated: ' + distanceLUse);
  }
  if (xfraction < 0) {
    logger.debug('Fraction trunkated: ' + distanceLUse);
    xfraction = 0;
  }
  x += xfraction * fullXDistancePixels;

  // Metadata may have limits per floor
  if (
    metadata.x_left_limits &&
    metadata.x_left_limits[item.floor ?? 0] &&
    x < metadata.x_left_limits[item.floor ?? 0] * imageScale
  ) {
    x = metadata.x_left_limits[item.floor ?? 0] * imageScale;
  }
  if (
    metadata.x_right_limits &&
    metadata.x_right_limits[item.floor ?? 0] &&
    x > metadata.x_right_limits[item.floor ?? 0] * imageScale
  ) {
    x = metadata.x_right_limits[item.floor ?? 0] * imageScale;
  }

  const itemsOnSamePosition: ItemPosition[] = []; // Itself
  let alarmOnSamePosition = false;
  if (Object.prototype.hasOwnProperty.call(levelItems, y)) {
    // Check if multiple items at this position
    for (let i = 0; i < levelItems[y].length; ++i) {
      const itemInfo = levelItems[y][i];
      if (
        itemInfo.x > x + metadata.marker_size * imageScale ||
        itemInfo.x < x - metadata.marker_size * imageScale
      )
        continue;
      x = itemInfo.x;
      if (showAsAlarm(itemInfo.item)) alarmOnSamePosition = true;
      itemsOnSamePosition.push(itemInfo);
    }
    // Add this item
    levelItems[y].push({ x, y, item });
  } else levelItems[y] = [{ x, y, item }];

  // Stagger devices that land at the same X on a different deck so they remain
  // individually tappable.  Record canonical x first so future items detect the
  // true position, not the visually-adjusted one.
  const xCanonical = x;
  x += computeCrossLevelStagger(
    Boolean(item.checkpoint),
    xCanonical,
    y,
    crossLevelItems,
    (metadata.marker_size || 15) * imageScale
  );
  crossLevelItems.push({ x: xCanonical, y });

  itemsOnSamePosition.push({ x, y, item });

  let hash = '';
  // The hash must include everything which makes the marker appear differently, including the underlying items state.
  itemsOnSamePosition.map((i) => {
    hash += i.item.alarmTime ? 'A' : '-';
    hash += i.item.checkinTime ? 'C' : '-';
    hash += formatDateAsAge(new Date(i.item.positionTime || new Date()));
    return null;
  });
  // logger.debug(hash);

  return (
    <SideMarker
      key={hash + height + item.id + itemsOnSamePosition.length + maxAge + x}
      item={item}
      metadata={metadata}
      xpos={x}
      ypos={y}
      selected={selected}
      setPopped={setPopped}
      imageScale={imageScale}
      width={width}
      height={height}
      maxAge={maxAge}
      alarmOnSamePosition={alarmOnSamePosition}
      itemsOnSamePosition={itemsOnSamePosition}
      areaSymbols={areaSymbols}
    />
  );
};

const Popped: React.FC<PoppedProps> = ({
  popped,
  setPopped,
  width,
  height,
  markerSize: _markerSize,
  setSelectedId,
  uncheckItem,
  setUncheckItem,
  checkItem,
  setCheckItem,
  currentCheckpoint,
  setCurrentCheckpoint,
  isChecker,
  isAdmin,
  showTraceButton,
  setTraceItem,
  traceItem,
  setClearTraceItem,
  clearTraceItem,
  setClearCheckpointCheckins,
}) => {
  const color = getDeviceColor(popped.item);
  useState(showAsAlarm(popped.item));
  useState(showAsChecked(popped.item));
  useState(showAsTrace(popped.item));

  const [state] = useState(JSON.stringify({ popped, width, height })); // Will make state change => redraw if props change?
  useEffect(() => {
    const timer = setTimeout(() => setPopped(null), 15000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPopped]);

  let leftRightStyle: React.CSSProperties = { left: popped.x + 'px' };
  let topBottomStyle: React.CSSProperties = { bottom: height - popped.y + 'px' };
  if (popped.x > width / 2) leftRightStyle = { right: width - popped.x + 'px' };
  if (popped.y < height / 2) topBottomStyle = { top: popped.y + 'px' };

  const displayAccuracy = false;

  const body = (
    <div
      key={state}
      onClick={() => setPopped(null)}
      style={{
        fontSize: smallFontSize,
        position: 'fixed',
        minWidth: '30vw',
        ...topBottomStyle,
        ...leftRightStyle,
        borderColor: color + '7',
        borderStyle: 'solid',
        borderWidth: '0.3vmin',
        background: '#fffd',
        color: 'black',
        borderRadius: '8px',
        padding: '6px',
      }}
    >
      {popped.item.checkpoint ? (
        <small>
          {currentCheckpoint === popped.item.id ? 'CURRENT ' : ''}CHECKPOINT
          <br />
        </small>
      ) : null}
      {popped.item.checkinTime ? (
        <div style={{ color: checkColor }}>
          <small>
            CHECKED AT CHECKPOINT
            <br />
          </small>
        </div>
      ) : null}
      <div style={{ fontWeight: 600 }}>{popped.item.title}</div>
      <small>{popped.item.text}</small>
      <br />
      <hr />
      {showAsTrace(popped.item) ? (
        <small>
          <b>
            <br />
            Tracking Activated
          </b>
        </small>
      ) : (
        ''
      )}
      <br />
      {showAsAlarm(popped.item) ? (
        <div style={{ color: alarmColor }}>
          {'Alarm age: ' + formatDateAsAge(new Date(popped.item.alarmTime || new Date()))}
        </div>
      ) : null}
      <br />
      {popped.item.timestamp
        ? 'Signal age: ' + formatDateAsAge(new Date(popped.item.timestamp))
        : null}
      <br />
      {popped.item.positionTime && !popped.item.checkpoint
        ? 'Position age: ' + formatDateAsAge(new Date(popped.item.positionTime))
        : null}
      <br />
      {showAsChecked(popped.item)
        ? 'Checkin age: ' + formatDateAsAge(new Date(popped.item.checkinTime || new Date()))
        : null}
      <br />
      {popped.item.output && popped.item.output.volts ? (
        <small>
          Battery: {isAdmin ? Math.round(popped.item.output.volts * 10) / 10 + 'V ' : ''}
          {isBatteryLow(popped.item as Partial<Device>) ? 'LOW' : 'OK'}
        </small>
      ) : null}
      {!popped.item.checkpoint ? <hr /> : null}
      {!popped.item.checkpoint ? (
        <small>
          Deck {popped.item.floor}
          {popped.item.area ? `, ${popped.item.area}` : ''}{' '}
          {displayAccuracy ? '- Accuracy ' + popped.item.accuracy + ' m' : ''}
          {/*
           * Show frame range only for measured positions.
           * Geofence and check-in positions resolve to a fixed anchor point
           * and do not represent a precise longitudinal location on the ship.
           * The ±10 tolerance reflects normal positioning uncertainty.
           */}
          {popped.item.frame != null &&
          popped.item.positionType !== 'GEOFENCE' &&
          popped.item.positionType !== 'checkin_station' ? (
            <>
              <br />
              Frame: {popped.item.frame - 10}–{popped.item.frame + 10}
            </>
          ) : null}
        </small>
      ) : null}
      {popped.item.positionTime && setSelectedId ? <hr /> : null}
      {isAdmin &&
      showTraceButton &&
      popped.item.positionTime &&
      setSelectedId &&
      !popped.item.checkpoint ? (
        <ClickOnceButton
          text="Show Trace"
          onClick={() => {
            window.localStorage.setItem('showAccuracy', 'false');
            setSelectedId(String(popped.item.id));
          }}
        />
      ) : null}
      {isChecker && showAsChecked(popped.item) && !uncheckItem ? (
        <ClickOnceButton
          text="Uncheck"
          onClick={() => {
            setUncheckItem(String(popped.item.id));
          }}
        />
      ) : null}
      {isChecker &&
      !showAsChecked(popped.item) &&
      currentCheckpoint &&
      !popped.item.checkpoint &&
      !checkItem ? (
        <ClickOnceButton
          text="Check in"
          onClick={() => {
            setCheckItem(String(popped.item.id));
          }}
        />
      ) : null}
      {isChecker &&
      !showAsAlarm(popped.item) &&
      !showAsTrace(popped.item) &&
      !popped.item.checkpoint &&
      !traceItem ? (
        <ClickOnceButton
          text="Begin Tracking"
          onClick={() => setTraceItem(String(popped.item.id))}
        />
      ) : null}
      {isChecker &&
      !showAsAlarm(popped.item) &&
      showAsTrace(popped.item) &&
      !popped.item.checkpoint &&
      !clearTraceItem ? (
        <ClickOnceButton
          text="Deactivate Tracking"
          onClick={() => setClearTraceItem(String(popped.item.id))}
        />
      ) : null}
      {isChecker && popped.item.checkpoint ? (
        <ClickOnceButton
          text="Clear all Checkins"
          onClick={() => {
            setClearCheckpointCheckins(String(popped.item.id));
          }}
        />
      ) : null}
      {isChecker && popped.item.checkpoint && currentCheckpoint !== popped.item.id ? (
        <ClickOnceButton
          text="Make Current"
          onClick={() => {
            setCurrentCheckpoint(String(popped.item.id));
          }}
        />
      ) : null}
      {isChecker && popped.item.checkpoint && currentCheckpoint === popped.item.id ? (
        <ClickOnceButton
          text="Clear Current"
          onClick={() => {
            setCurrentCheckpoint(null);
          }}
        />
      ) : null}
    </div>
  );

  return <div>{body}</div>;
};

export const SideWays: React.FC<SideWaysProps> = (props) => {
  const [metadata, setMetadata] = useState<SidewaysMetadata | null>(null);
  const [popped, setPopped] = useState<PoppedItem | null>(null);
  const [areaSymbols, setAreaSymbols] = useState<Record<string, string>>({});
  const [uncheckItem, setUncheckItem] = useState<string | null>(null);
  const [checkItem, setCheckItem] = useState<string | null>(null);
  const [traceItem, setTraceItem] = useState<string | null>(null);
  const [clearTraceItem, setClearTraceItem] = useState<string | null>(null);
  const [clearCheckpointCheckins, setClearCheckpointCheckins] = useState<string | null>(null);

  const {
    series,
    isChecker,
    isAdmin,
    showTraceButton = true,
    setSelected,
    currentCheckpoint,
    setCurrentCheckpoint,
    setSelectedId,
  } = props;
  const [selected] = useState<string | null>(props.selected ?? null);
  const [maxAge] = useState<number>(props.maxAge);
  const [tracing] = useState<boolean | undefined>(props.tracing);

  useEffect(() => {
    geofenceAreaConfigAPI
      .list()
      .then((res) => {
        const map: Record<string, string> = {};
        res.configs.forEach((c) => {
          if (c.symbol_key) map[c.area_name] = c.symbol_key;
        });
        setAreaSymbols(map);
      })
      .catch(() => {
        /* silently ignore — symbols are decorative */
      });
  }, []);

  let checkCount = 0;
  let alarmCount = 0;
  let traceCount = 0;
  let itemCount = 0;
  series &&
    series.map((i) => {
      itemCount++;
      if (showAsAlarm(i)) alarmCount++;
      if (showAsChecked(i)) checkCount++;
      if (showAsTrace(i)) traceCount++;
      return null;
    });
  useState(itemCount);
  useState(checkCount);
  useState(alarmCount);
  useState(traceCount);

  const [windowDimensions, setWindowDimensions] = useState<WindowDimensions>(getWindowDimensions());
  useEffect(() => {
    function handleResize(): void {
      setWindowDimensions(getWindowDimensions());
    }

    const ignore = (e: Event): void => {
      e.preventDefault();
    };

    // Prevent pinch-zoom on multi-touch devices (Android + iOS)
    const preventPinch = (e: TouchEvent): void => {
      if (e.touches.length > 1) e.preventDefault();
    };

    // CSS touch-action is the most reliable way to prevent pinch-zoom on modern iOS Safari
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.touchAction = 'pan-x pan-y';

    window.addEventListener('dragstart', ignore, { passive: false });
    window.addEventListener('wheel', ignore, { passive: false });
    // Prevent iOS Safari viewport zoom (gesturestart/gesturechange are Safari-specific)
    window.addEventListener('gesturestart', ignore, { passive: false });
    window.addEventListener('gesturechange', ignore, { passive: false });
    // Prevent pinch-zoom via multi-touch (cross-browser)
    window.addEventListener('touchmove', preventPinch, { passive: false });
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      document.body.style.touchAction = prevTouchAction;
      window.removeEventListener('wheel', ignore);
      window.removeEventListener('dragstart', ignore);
      window.removeEventListener('gesturestart', ignore);
      window.removeEventListener('gesturechange', ignore);
      window.removeEventListener('touchmove', preventPinch);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const width = windowDimensions ? windowDimensions.width : 100;
  const height = windowDimensions ? windowDimensions.height : 80;

  let fetcher: ReactElement | null = null;
  if (!metadata)
    fetcher = (
      <Fetch
        key={'fetch-metadata'}
        submit={true}
        onError={() => {
          setMetadata({ error: 'Failed to load metadata' } as SidewaysMetadata);
        }}
        onResult={(r: unknown) => {
          logger.debug('Received metadata: ', r);
          setMetadata(r as SidewaysMetadata);
        }}
        api={getApi() + `/sidewaysdata?token=${ec(window.localStorage.getItem('token') || '')}`}
      />
    );

  if (fetcher) {
    return <div style={{ ...smallTextStyle }}>{fetcher}Loading...</div>;
  }
  if (metadata && metadata.error) {
    return <div>{metadata.error}</div>;
  }

  if (!metadata || !metadata.image_w) {
    return <div>Failed to load metadata</div>;
  }
  logger.debug('Window dimenstions (w,h): ', width, height);
  const imageScale = (width / metadata.image_w) * 1.0;
  const imageTop = height / 2 - (imageScale * metadata.image_h) / 2;

  const referenceDistance = calculateDistance(
    metadata.reference_lat_left,
    metadata.reference_lon_left,
    metadata.reference_lat_right,
    metadata.reference_lon_right
  );
  const warning = null;
  if (
    referenceDistance > 1.1 * metadata.actual_length_m ||
    referenceDistance < 0.9 * metadata.actual_length_m
  ) {
    //    warning = <div style={{fontSize:smallFontSize}}>Warning: The displayed object is {Math.round(metadata.actual_length_m)} m, but the left to right reference points distance is {Math.round(referenceDistance)} m</div>
    logger.debug(
      `Warning: The displayed object is ${Math.round(metadata.actual_length_m)} m, but the left to right reference points distance is ${Math.round(referenceDistance)} m`
    );
  } else {
    // warning = <div>The displayed object is {Math.round(metadata.actual_length_m)} m, left to right reference points distance is {Math.round(referenceDistance)} m</div>;
  }

  const levelItems: Record<number, ItemPosition[]> = {};
  const crossLevelItems: Array<{ x: number; y: number }> = [];

  const getSeriesName = (id: string | null): string => {
    let name = 'Unknown';
    if (!id) return name;
    series &&
      series.map((i) => {
        if (i.id === id) name = i.title;
        return null;
      });
    return name;
  };

  const sortStatus = (seriesArr: BaseMapItem[]): BaseMapItem[] => {
    const tmp = [...seriesArr];
    return tmp.sort((a, b) => {
      if (a.checkpoint && !b.checkpoint) return 1;
      if (b.checkpoint && !a.checkpoint) return -1;

      const aCheck = showAsChecked(a);
      const bCheck = showAsChecked(b);
      if (aCheck && !bCheck) return 1;
      if (bCheck && !aCheck) return -1;
      const aAlarm = showAsAlarm(a);
      const bAlarm = showAsAlarm(b);
      if (aAlarm && !bAlarm) return -1;
      if (bAlarm && !aAlarm) return 1;
      return a.title > b.title ? 1 : -1;
    });
  };

  const filter = (seriesArr: BaseMapItem[]): BaseMapItem[] => {
    if (!tracing) return seriesArr;
    const result: BaseMapItem[] = [];
    seriesArr.map((item) => {
      if (getDisplayReplacedPositions()) {
        // Viewing unfiltered positions
        if (item.positionReplaced)
          // Show the replaced position
          result.push(item);
        if (!item.positionFiltered)
          // Do not show the filtered position
          result.push(item);
      } else {
        // This is the "normal path"
        if (!item.positionReplaced)
          // Show the replaced position
          result.push(item);
      }
      return null;
    });
    return result;
  };

  return (
    <div
      key={'sideways-' + height + '-' + width}
      style={{
        position: 'fixed',
        top: imageTop + 'px',
        left: '0pt',
        width: '100%',
        // Prevents pinch-zoom on iOS 16+ where JS gesture event prevention is no longer reliable.
        touchAction: 'pan-x pan-y',
      }}
      onClick={() => setPopped(null)}
    >
      <img
        key={'image-' + height + '-' + width}
        width="100%"
        src={getApi() + `/sidewaysimage?token=${ec(window.localStorage.getItem('token') || '')}`}
        alt="Side view"
      />
      <div
        key={'items-' + height + '-' + width}
        style={{ fontSize: smallFontSize, textAlign: 'left' }}
      >
        {series &&
          filter(sortStatus(series)).map((item) =>
            generateItem(
              metadata,
              item,
              imageScale,
              imageTop,
              setPopped,
              levelItems,
              crossLevelItems,
              width,
              height,
              selected,
              setSelected,
              maxAge,
              areaSymbols
            )
          )}
      </div>
      {popped ? (
        <Popped
          key={
            'popped' +
            height +
            popped.item.id +
            popped.item.checkinTime +
            popped.item.alarmTime +
            popped.item.traceTime
          }
          popped={popped}
          setPopped={setPopped}
          markerSize={imageScale * metadata.marker_size}
          width={width}
          height={height}
          setSelectedId={setSelectedId}
          isChecker={isChecker}
          isAdmin={isAdmin}
          showTraceButton={showTraceButton}
          setUncheckItem={setUncheckItem}
          uncheckItem={uncheckItem}
          setCheckItem={setCheckItem}
          checkItem={checkItem}
          setTraceItem={setTraceItem}
          traceItem={traceItem}
          setClearTraceItem={setClearTraceItem}
          clearTraceItem={clearTraceItem}
          setCurrentCheckpoint={setCurrentCheckpoint}
          currentCheckpoint={currentCheckpoint}
          setClearCheckpointCheckins={setClearCheckpointCheckins}
        />
      ) : null}
      <div
        key="warning"
        style={{ position: 'fixed', bottom: '14vh', left: '1vh', fontSize: '2vmin', color: '#000' }}
      >
        {warning}
      </div>
      {uncheckItem ? (
        <Fetch
          key={'fetch-uncheck-' + uncheckItem}
          submit={true}
          method="POST"
          body={{ device: uncheckItem }}
          onError={() => setUncheckItem(null)}
          onResult={() => setUncheckItem(null)}
          api={getApi() + '/uncheck'}
        />
      ) : null}
      {checkItem && currentCheckpoint ? (
        <Fetch
          key={'fetch-check-' + checkItem}
          submit={true}
          method="POST"
          body={{ device: checkItem, checkpoint: currentCheckpoint }}
          onError={() => setCheckItem(null)}
          onResult={() => setCheckItem(null)}
          api={getApi() + '/checkin'}
        />
      ) : null}
      {traceItem ? (
        <Fetch
          key={'fetch-trace-' + traceItem}
          submit={true}
          method="POST"
          body={{ device: traceItem, port: 2, data: '9100000001' }}
          onError={() => setTraceItem(null)}
          onResult={() => setTraceItem(null)}
          api={getApi() + '/downlink'}
        />
      ) : null}
      {clearTraceItem ? (
        <Fetch
          key={'fetch-clear-trace-' + clearTraceItem}
          submit={true}
          method="POST"
          body={{ device: clearTraceItem, port: 2, data: '9100000000' }}
          onError={() => setClearTraceItem(null)}
          onResult={() => setClearTraceItem(null)}
          api={getApi() + '/downlink'}
        />
      ) : null}
      {clearCheckpointCheckins ? (
        <Fetch
          key={'fetch-check-' + clearCheckpointCheckins}
          submit={true}
          method="POST"
          body={{ checkpoint: clearCheckpointCheckins }}
          onError={() => setClearCheckpointCheckins(null)}
          onResult={() => setClearCheckpointCheckins(null)}
          api={getApi() + '/clearcheckins'}
        />
      ) : null}
      {checkItem && (
        <div style={smallTextStyle}>
          Checking in {getSeriesName(checkItem)} to {getSeriesName(currentCheckpoint)}
        </div>
      )}
      {checkItem && <div style={smallTextStyle}>Unchecking in {getSeriesName(uncheckItem)}</div>}
      {clearCheckpointCheckins && (
        <div style={smallTextStyle}>
          Clearing all checkins for {getSeriesName(clearCheckpointCheckins)}
        </div>
      )}
    </div>
  );
};
